import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Grid, Paper, Avatar, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import Hero from '../../sections/Hero';
import TestimonialsSection from '../../sections/TestimonialsSection';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  borderRadius: theme.shape.borderRadius,
}));

const AboutPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About Us</title>
        <meta
          name="description"
          content="Learn about JBR Delivery LLC, our mission, values, and commitment to providing exceptional delivery, junk removal, and moving services in Northeast Wisconsin."
        />
      </Helmet>
      
      <Hero 
        title="About JBR Delivery LLC" 
        subtitle="Learn about our company, our mission, and the dedicated team behind our trusted delivery, junk removal, and moving services."
        ctaText="Contact Us"
        showQuickForm={false}
      />
      
      <Box sx={{ py: 8 }}>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="h2" component="h2" gutterBottom fontWeight={700}>
                Our Story
              </Typography>
              
              <Typography variant="body1" paragraph>
                JBR Delivery LLC was founded with a simple mission: to provide reliable, efficient, and customer-focused delivery, junk removal, and moving services to the Northeast Wisconsin community. What began as a small operation has grown into a trusted name in the region, known for our commitment to quality service and customer satisfaction.
              </Typography>
              
              <Typography variant="body1" paragraph>
                As a locally owned and operated business, we understand the unique needs of our community. Our team consists of skilled professionals who are not just employees but neighbors dedicated to making life easier for fellow residents through our services.
              </Typography>
              
              <Typography variant="h4" component="h3" gutterBottom fontWeight={600} sx={{ mt: 5 }}>
                Our Mission
              </Typography>
              
              <Typography variant="body1" paragraph>
                At JBR Delivery Service, our mission is to provide efficient, reliable, and eco-friendly solutions for all of your delivery, junk removal, and moving needs. We strive to simplify your life by offering professional services that are quick, affordable, and tailored to your specific needs, ensuring a smooth and stress-free experience every time.
              </Typography>
              
              <Typography variant="body1" paragraph>
                Whether you're moving into a new home, decluttering, or seeking delivery assistance, we are dedicated to helping you get things done with care, commitment, and a customer-first approach.
              </Typography>
              
              <Typography variant="h4" component="h3" gutterBottom fontWeight={600} sx={{ mt: 5 }}>
                Our Values
              </Typography>
              
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={3}>
                    <VerifiedIcon color="primary" sx={{ fontSize: 30, mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="h6" component="h4" gutterBottom>
                        Reliability
                      </Typography>
                      <Typography variant="body2">
                        We show up on time, every time, and complete our work as promised.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={3}>
                    <VerifiedIcon color="primary" sx={{ fontSize: 30, mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="h6" component="h4" gutterBottom>
                        Quality
                      </Typography>
                      <Typography variant="body2">
                        We take pride in our work and ensure every job is completed to the highest standards.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={3}>
                    <VerifiedIcon color="primary" sx={{ fontSize: 30, mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="h6" component="h4" gutterBottom>
                        Integrity
                      </Typography>
                      <Typography variant="body2">
                        We operate with honesty and transparency in all our customer and business interactions.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={3}>
                    <VerifiedIcon color="primary" sx={{ fontSize: 30, mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="h6" component="h4" gutterBottom>
                        Customer Focus
                      </Typography>
                      <Typography variant="body2">
                        Your satisfaction is our top priority, and we tailor our services to meet your specific needs.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <StyledPaper elevation={3}>
                <Box textAlign="center" mb={4}>
                  <Typography variant="h4" component="h3" gutterBottom fontWeight={600}>
                    Company Information
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Established:
                  </Typography>
                  <Typography variant="body1">
                    2022
                  </Typography>
                </Box>
                
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Service Area:
                  </Typography>
                  <Typography variant="body1">
                    Northeast Wisconsin
                  </Typography>
                </Box>
                
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Phone Numbers:
                  </Typography>
                  <Typography variant="body1">
                    (920) 255-8952
                  </Typography>
                  <Typography variant="body1">
                    (920) 265-4729
                  </Typography>
                </Box>
                
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Email:
                  </Typography>
                  <Typography variant="body1">
                    jbrdeliveryllc@gmail.com
                  </Typography>
                </Box>
                
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Business Hours:
                  </Typography>
                  <Typography variant="body2">
                    Monday - Friday: 8 AM - 6 PM
                  </Typography>
                  <Typography variant="body2">
                    Saturday: 9 AM - 4 PM
                  </Typography>
                  <Typography variant="body2">
                    Sunday: Closed
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <Box textAlign="center">
                  <Typography variant="subtitle1" gutterBottom>
                    Fully Licensed & Insured
                  </Typography>
                </Box>
              </StyledPaper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <TestimonialsSection />
    </>
  );
};

export default AboutPage;
